import React, { useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, FileText, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { useApiQuery } from '../../hooks/useApi';
import { notificationService } from '../../services/api';
import FormModalLayout from '../../components/Common/FormModalLayout';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { RichTextEditor, RichTextHtml, ensureRichTextValue, isEmptyRichText } from '../../components/RichTextEditor';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';

const CHANNEL_LABELS = {
  email: 'E-mail (HTML)',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
};

const SAMPLE_VARS = `{
  "nome": "Maria Silva",
  "empresa": "Eco Ltda",
  "licenca_numero": "2024/001",
  "data": "13/06/2026"
}`;

const NotificationTemplates = () => {
  const { t } = useTranslation('notifications');

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterTypeId, setFilterTypeId] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewVarsText, setPreviewVarsText] = useState(SAMPLE_VARS);

  const [form, setForm] = useState({
    notification_type_id: '',
    channel: 'email',
    name: '',
    subject: '',
    body_html: '',
    body_text: '',
    is_default: false,
  });

  const { data: typesRaw, refetch: refetchTypes } = useApiQuery('notification-types', async () => {
    const r = await notificationService.listTypes();
    return r.data;
  });

  const types = Array.isArray(typesRaw) ? typesRaw : typesRaw?.data ?? [];

  const templateParams = useMemo(() => {
    const p = {};
    if (filterTypeId) p.notification_type_id = filterTypeId;
    if (filterChannel) p.channel = filterChannel;
    return p;
  }, [filterTypeId, filterChannel]);

  const { data: templatesRaw, isLoading, refetch: refetchTemplates } = useApiQuery(
    ['notification-templates', templateParams],
    async () => {
      const r = await notificationService.listTemplates(templateParams);
      return r.data;
    }
  );
  const templates = Array.isArray(templatesRaw) ? templatesRaw : templatesRaw?.data ?? [];

  const resetForm = () => {
    setForm({
      notification_type_id: types[0]?.id || '',
      channel: 'email',
      name: '',
      subject: '',
      body_html: '',
      body_text: '',
      is_default: false,
    });
  };

  const handleCreate = () => {
    setEditingItem(null);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({
      notification_type_id: item.notification_type_id || '',
      channel: item.channel || 'email',
      name: item.name || '',
      subject: item.subject || '',
      body_html: ensureRichTextValue(item.body_html || ''),
      body_text: item.body_text || '',
      is_default: !!item.is_default,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este template?')) return;
    try {
      await notificationService.deleteTemplate(id);
      toast.success('Template excluído');
      refetchTemplates();
    } catch (e) {
      toast.error(userFacingError(e, 'deleteError'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.notification_type_id || !form.name.trim()) {
      toast.error(t('fillTypeAndName'));
      return;
    }
    if (form.channel === 'email' && !form.subject.trim()) {
      toast.error(t('emailSubjectRequired'));
      return;
    }
    const bodyText =
      form.channel === 'email' && isEmptyRichText(form.body_html)
        ? form.body_text.trim()
        : form.body_text.trim() ||
          (form.body_html ? form.body_html.replace(/<[^>]+>/g, ' ').trim() : '');
    if (!bodyText) {
      toast.error(t('bodyRequired'));
      return;
    }
    const payload = {
      notification_type_id: form.notification_type_id,
      channel: form.channel,
      name: form.name.trim(),
      subject: form.channel === 'email' ? form.subject.trim() : null,
      body_html: form.channel === 'email' && !isEmptyRichText(form.body_html) ? form.body_html : null,
      body_text: bodyText,
      is_default: form.is_default,
    };
    try {
      if (editingItem) {
        await notificationService.updateTemplate(editingItem.id, payload);
        toast.success('Template atualizado');
      } else {
        await notificationService.createTemplate(payload);
        toast.success('Template criado');
      }
      setShowModal(false);
      setEditingItem(null);
      refetchTemplates();
      refetchTypes();
    } catch (err) {
      toast.error(userFacingError(err, 'saveError'));
    }
  };

  const handlePreview = async () => {
    let variables = {};
    try {
      variables = JSON.parse(previewVarsText || '{}');
    } catch {
      toast.error(t('invalidVariables'));
      return;
    }
    try {
      const r = await notificationService.previewTemplate({
        subject: form.subject,
        body_html: form.body_html,
        body_text: form.body_text,
        variables,
      });
      setPreviewData(r.data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(userFacingError(e, 'previewError'));
    }
  };

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("templatesTitle")}
          description="Configure mensagens estilo maladireta com variáveis {{nome}}, {{licenca_numero}}, etc. Cada tipo de notificação pode ter templates por canal (e-mail, WhatsApp, SMS)."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Notificações', to: '/notifications' },
            { label: 'Templates' },
          ]}
        >
          <Can permission={P.NOTIFICATIONS_MANAGE}>
            <button type="button" onClick={handleCreate} className="btn btn-primary inline-flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Novo template
            </button>
          </Can>
        </PageHeader>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="form-group">
              <label className="form-label">Filtrar por tipo</label>
              <select className="form-select" value={filterTypeId} onChange={(e) => setFilterTypeId(e.target.value)}>
                <option value="">Todos</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Filtrar por canal</label>
              <select className="form-select" value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)}>
                <option value="">Todos</option>
                {Object.entries(CHANNEL_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Nome</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Tipo</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Canal</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Padrão</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                    Carregando…
                  </td>
                </tr>
              ) : templates.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                    Nenhum template cadastrado. Os tipos padrão são criados automaticamente na primeira consulta.
                  </td>
                </tr>
              ) : (
                templates.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50/80">
                    <td className="px-4 py-3 font-medium text-gray-900">{t.name}</td>
                    <td className="px-4 py-3 text-gray-600">{t.notification_type_name || t.notification_type_code}</td>
                    <td className="px-4 py-3 text-gray-600">{CHANNEL_LABELS[t.channel] || t.channel}</td>
                    <td className="px-4 py-3">{t.is_default ? 'Sim' : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <Can permission={P.NOTIFICATIONS_MANAGE}>
                        <div className="inline-flex gap-1">
                          <button type="button" className="btn btn-outline btn-xs" onClick={() => handleEdit(t)}>
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button type="button" className="btn btn-outline btn-xs text-red-700" onClick={() => handleDelete(t.id)}>
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </Can>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-lg border border-primary-100 bg-primary-50/50 p-4 text-sm text-gray-700">
          <p className="font-medium text-primary-900">Variáveis disponíveis (maladireta)</p>
          <p className="mt-1">
            Use <code className="rounded bg-white px-1">{'{{nome}}'}</code>,{' '}
            <code className="rounded bg-white px-1">{'{{empresa}}'}</code>,{' '}
            <code className="rounded bg-white px-1">{'{{licenca_numero}}'}</code>,{' '}
            <code className="rounded bg-white px-1">{'{{data}}'}</code> e outras chaves no assunto e no corpo.
            Na tela de envio, preencha os valores correspondentes.
          </p>
        </div>
      </div>

      {showModal && (
        <FormModalLayout
          title={editingItem ? 'Editar template' : 'Novo template'}
          subtitle={t("templatesSubtitle")}
          headerIcon={FileText}
          onClose={() => {
            setShowModal(false);
            setEditingItem(null);
          }}
          size="4xl"
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                Cancelar
              </button>
              <button type="button" className="btn btn-outline inline-flex items-center gap-2" onClick={handlePreview}>
                <Eye className="h-4 w-4" />
                Pré-visualizar
              </button>
              <button type="submit" form="template-form" className="btn btn-primary">
                Salvar
              </button>
            </>
          }
        >
          <form id="template-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="form-group">
                <label className="form-label form-label-required">Tipo de notificação</label>
                <select
                  className="form-select"
                  value={form.notification_type_id}
                  onChange={(e) => setForm((f) => ({ ...f, notification_type_id: e.target.value }))}
                  required
                >
                  <option value="">Selecione…</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label form-label-required">Canal</label>
                <select
                  className="form-select"
                  value={form.channel}
                  onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
                >
                  {Object.entries(CHANNEL_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group md:col-span-2">
                <label className="form-label form-label-required">Nome do template</label>
                <input
                  className="form-input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Ex: Licença a vencer — e-mail padrão"
                />
              </div>
              {form.channel === 'email' ? (
                <div className="form-group md:col-span-2">
                  <label className="form-label form-label-required">Assunto do e-mail</label>
                  <input
                    className="form-input"
                    value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    placeholder="Ex: Licença {{licenca_numero}} próxima do vencimento"
                  />
                </div>
              ) : null}
              {form.channel === 'email' ? (
                <div className="form-group md:col-span-2">
                  <label className="form-label">Corpo HTML (editor)</label>
                  <RichTextEditor
                    value={form.body_html}
                    onChange={(v) => setForm((f) => ({ ...f, body_html: v }))}
                  />
                </div>
              ) : null}
              <div className="form-group md:col-span-2">
                <label className="form-label form-label-required">
                  {form.channel === 'email' ? 'Texto alternativo (plain)' : 'Corpo da mensagem'}
                </label>
                <textarea
                  className="form-input min-h-[120px] font-mono text-sm"
                  value={form.body_text}
                  onChange={(e) => setForm((f) => ({ ...f, body_text: e.target.value }))}
                  placeholder={
                    form.channel === 'whatsapp'
                      ? 'Olá {{nome}}, a licença {{licenca_numero}} vence em {{data}}.'
                      : 'Corpo da mensagem com variáveis…'
                  }
                />
              </div>
              <div className="form-group md:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_default}
                    onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
                  />
                  Template padrão para este tipo e canal
                </label>
              </div>
              <div className="form-group md:col-span-2">
                <label className="form-label">Variáveis para pré-visualização (JSON)</label>
                <textarea
                  className="form-input min-h-[80px] font-mono text-xs"
                  value={previewVarsText}
                  onChange={(e) => setPreviewVarsText(e.target.value)}
                />
              </div>
            </div>
          </form>
        </FormModalLayout>
      )}

      {previewOpen && previewData ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Pré-visualização</h3>
            {previewData.subject ? (
              <p className="mt-3 text-sm">
                <span className="font-medium text-gray-600">Assunto:</span> {previewData.subject}
              </p>
            ) : null}
            {previewData.body_html ? (
              <div className="mt-4 rounded-lg border border-gray-200 p-4">
                <RichTextHtml html={previewData.body_html} />
              </div>
            ) : null}
            {previewData.body_text ? (
              <pre className="mt-4 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-800">
                {previewData.body_text}
              </pre>
            ) : null}
            <div className="mt-4 text-right">
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setPreviewOpen(false)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </FormPageShell>
  );
};

export default NotificationTemplates;
